import React, { useState, useEffect, useMemo } from 'react'
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons'
import { SessionStateSummary } from './types'
import { getSessionStateSummary } from '../../../../../../../../../../crud/management_api/local_sessions'
import './StateSummary.scss'
import MiniLoadingSpinner from 'components/Spinners/MiniLoadingSpinner'

interface StateSummaryProps {
    show: boolean;
    localSessionId: number | null;
    onLoadingChange?: (loading: boolean) => void;
}

export const StateSummary: React.FC<StateSummaryProps> = ({ show, localSessionId, onLoadingChange }) => {
    const [loading, setLoading] = useState(false)
    const [summary, setSummary] = useState<SessionStateSummary | null>(null)
    const [open, setOpen] = useState<Record<string, boolean>>({})

    useEffect(() => {
    if (show && localSessionId) {
        setLoading(true)
        onLoadingChange?.(true)
            getSessionStateSummary(localSessionId)
                .then(res => {
                    setSummary(res.data)
            setLoading(false)
            onLoadingChange?.(false)
                })
                .catch(() => {
                    setSummary(null)
            setLoading(false)
            onLoadingChange?.(false)
                })
        } else {
            setSummary(null)
            onLoadingChange?.(false)
        }
    }, [show, localSessionId, onLoadingChange])

    const sections = useMemo(
        () => [
            { key: 'pedidos', label: 'Pedidos', countKey: 'pedidos_count', listKey: 'pedidos_list' },
            { key: 'comandas', label: 'Comandas', countKey: 'comandas_count', listKey: 'comandas_list' },
            { key: 'pedido_modifications', label: 'Modificaciones de pedidos', countKey: 'pedido_modifications_count', listKey: 'pedido_modifications_list' },
            { key: 'receipts', label: 'Recibos', countKey: 'receipts_count', listKey: 'receipts_list' },
            { key: 'receipt_links', label: 'Vínculos de recibo', countKey: 'receipt_links_count', listKey: 'receipt_links_list' },
            { key: 'transactions', label: 'Transacciones', countKey: 'transactions_count', listKey: 'transactions_list' },
            { key: 'caja_movements', label: 'Movimientos de caja', countKey: 'caja_movements_count', listKey: 'caja_movements_list' },
            { key: 'caja_session_records', label: 'Sesiones de caja', countKey: 'caja_session_records_count', listKey: 'caja_session_records_list' },
            { key: 'mermas', label: 'Mermas', countKey: 'mermas_count', listKey: 'mermas_list' },
            { key: 'storage_movements', label: 'Movimientos de almacén', countKey: 'storage_movements_count', listKey: 'storage_movements_list' },
            { key: 'stock_shortages', label: 'Faltantes de stock', countKey: 'stock_shortages_count', listKey: 'stock_shortages_list' },
            { key: 'local_sales_stats', label: 'Estadísticas de ventas (local)', countKey: 'local_sales_stats_count', listKey: 'local_sales_stats_list' },
            { key: 'user_sales_stats', label: 'Estadísticas de ventas (usuarios)', countKey: 'user_sales_stats_count', listKey: 'user_sales_stats_list' },
            { key: 'pedido_type_stats', label: 'Estadísticas por tipo de pedido', countKey: 'pedido_type_stats_count', listKey: 'pedido_type_stats_list' },
        ],
        []
    )

    const toggle = (k: string) => setOpen(s => ({ ...s, [k]: !s[k] }))

    const renderItem = (sectionKey: string, item: any, idx: number) => {
        switch (sectionKey) {
            case 'pedidos':
                return (
                    <li key={idx} className="item-row">
                        <span className="item-id">Pedido #{item.pedido_id}</span>
                        <span className="item-meta">Mesa: {item.mesa_id ?? '—'}</span>
                        <span className="item-flags">
                            {item.cancelled ? 'Cancelado' : item.closed ? 'Cerrado' : 'Abierto'}
                            {item.receipt_id ? ` • Recibo ${item.receipt_id}` : ''}
                        </span>
                    </li>
                )
            case 'comandas':
                return (
                    <li key={idx} className="item-row">
                        <span className="item-id">Comanda #{item.comanda_id}</span>
                        <span className="item-meta">Pedido: {item.pedido_id}</span>
                        <span className="item-flags">{item.printed ? 'Impresa' : 'No impresa'} • {item.isReady ? 'Lista' : 'Pendiente'} • {item.isDelivered ? 'Entregada' : 'Sin entregar'}</span>
                    </li>
                )
            case 'pedido_modifications':
                return (
                    <li key={idx} className="item-row">
                        <span className="item-id">Modificación #{item.modification_id}</span>
                        <span className="item-meta">{item.isnegative ? 'Descuento' : 'Recargo'} {item.modification_amount ?? ''}</span>
                    </li>
                )
            case 'receipts':
                return (
                    <li key={idx} className="item-row">
                        <span className="item-id">Recibo #{item.receipt_id}</span>
                        <span className="item-meta">Pagado: {item.total_payed ?? 0} • Adeudado: {item.total_due ?? 0}</span>
                        <span className="item-flags">{item.active ? 'Activo' : 'Inactivo'}{item.is_cancelled ? ' • Cancelado' : ''}</span>
                    </li>
                )
            case 'receipt_links':
                return (
                    <li key={idx} className="item-row">
                        <span className="item-id">Recibo #{item.receipt_id}</span>
                        <span className="item-meta">Transacción #{item.transaction_id}</span>
                    </li>
                )
            case 'transactions':
                return (
                    <li key={idx} className="item-row">
                        <span className="item-id">Tx #{item.transaction_id}</span>
                        <span className="item-meta">Tipo: {item.payment_type_id}</span>
                        <span className="item-flags">{item.transaction_amount ?? 0} • {item.approved ? 'Aprobada' : 'Rechazada'}{item.canceled ? ' • Cancelada' : ''}</span>
                    </li>
                )
            case 'caja_movements':
                return (
                    <li key={idx} className="item-row">
                        <span className="item-id">Movimiento #{item.caja_movement_id}</span>
                        <span className="item-meta">{item.is_positive ? '+' : '-'}{item.amount ?? 0}</span>
                    </li>
                )
            case 'caja_session_records':
                return (
                    <li key={idx} className="item-row">
                        <span className="item-id">Sesión #{item.caja_session_record_id}</span>
                        <span className="item-meta">Inicial: {item.initial_amount ?? 0} • Final: {item.final_amount ?? 0}</span>
                    </li>
                )
            case 'mermas':
                return (
                    <li key={idx} className="item-row">
                        <span className="item-id">Merma #{item.merma_id}</span>
                        <span className="item-meta">Insumo: {item.ingredient_id ?? '—'} • Cant.: {item.quantity ?? 0}</span>
                    </li>
                )
            case 'storage_movements':
                return (
                    <li key={idx} className="item-row">
                        <span className="item-id">Movimiento #{item.movement_id}</span>
                        <span className="item-meta">Insumo: {item.ingredient_id ?? '—'} • Item: {item.item_id ?? '—'}</span>
                    </li>
                )
            case 'stock_shortages':
                return (
                    <li key={idx} className="item-row">
                        <span className="item-id">Faltante #{item.shortage_id}</span>
                        <span className="item-meta">Insumo: {item.ingredient_id ?? '—'} • Item: {item.item_id ?? '—'} • Cant.: {item.quantity ?? 0}</span>
                        <span className="item-flags">{item.solved ? 'Resuelto' : 'Pendiente'}</span>
                    </li>
                )
            case 'local_sales_stats':
                return (
                    <li key={idx} className="item-row">
                        <span className="item-id"># {item.sales_total_id}</span>
                        <span className="item-meta">Brutas: {item.gross_sales ?? 0} • Netas: {item.net_sales ?? 0}</span>
                        <span className="item-flags">Recibos válidos: {item.valid_receipt_count ?? 0}</span>
                    </li>
                )
            case 'user_sales_stats':
                return (
                    <li key={idx} className="item-row">
                        <span className="item-id">Usuario #{item.user_id}</span>
                        <span className="item-meta">Total: {item.total_sales ?? 0} • Recibos: {item.receipt_count ?? 0}</span>
                    </li>
                )
            case 'pedido_type_stats':
                return (
                    <li key={idx} className="item-row">
                        <span className="item-id">Tipo #{item.pedido_type_id}</span>
                        <span className="item-meta">Total: {item.total_sales ?? 0} • Recibos: {item.receipt_count ?? 0}</span>
                    </li>
                )
            default:
                return (
                    <li key={idx} className="item-row">
                        <span className="item-id">Elemento</span>
                    </li>
                )
        }
    }

    if (!show) return null

    if (loading && !summary) return <MiniLoadingSpinner />

    return (
        <div className="session-state-summary-container">
            <div className="summary-header">
                <h5>Se eliminarán los siguientes registros</h5>
            </div>
            {!loading && summary && (
                <div className="sections-list">
                    {sections.map(sec => {
                        const count = (summary as any)[sec.countKey] as number
                        const list = (summary as any)[sec.listKey] as any[]
                        const disabled = !count || count <= 0
                        const isOpen = !!open[sec.key]
                        return (
                            <div key={sec.key} className={`section-row${disabled ? ' disabled' : ''}${isOpen ? ' open' : ''}`}>
                                <button
                                    type="button"
                                    className="section-toggle"
                                    onClick={() => !disabled && toggle(sec.key)}
                                    disabled={disabled}
                                >
                                    <span className="label">{sec.label}</span>
                                    <span className="count-badge">{count || 0}</span>
                                    <span className="chevron">{isOpen ? <ChevronUp /> : <ChevronDown />}</span>
                                </button>
                                {isOpen && list && list.length > 0 && (
                                    <ul className="items">
                                        {list.map((it, idx) => renderItem(sec.key, it, idx))}
                                    </ul>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
            {!loading && summary && summary.has_related_records === false && (
                <div className="empty">No hay registros relacionados.</div>
            )}
        </div>
    )
}

export default StateSummary;

